import { useState, useEffect } from "react";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";
import { Button } from "../../../components/Button";

/**
 * End reason options - labels and API values per Figma design (node 8168-13917).
 * When endReason is sent to the API, no report is generated.
 */
export const END_REASON_OPTIONS: { label: string; value: string }[] = [
  { label: "Interruption", value: "interruption" },
  { label: "Technical issue", value: "technical_issue" },
  { label: "Didn't feel ready yet", value: "didnt_feel_ready_yet" },
  { label: "Want to restart", value: "want_to_restart" },
  { label: "Call felt off/ AI issue", value: "call_felt_off_ai_issue" },
  { label: "Other", value: "other" },
];

export function EndSessionModal({
  isOpen,
  onClose,
  onEndSession,
  onError,
  loading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onEndSession: (endReason: string) => void | Promise<void>;
  /** Called when ending fails *before* onDisconnect (e.g. getAudioRecordings, stream destruction).
   * Note: API errors inside onDisconnect are handled there and never reach this callback. */
  onError?: (err: unknown) => void;
  loading?: boolean;
}) {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [isEnding, setIsEnding] = useState(false);

  const handleEndSession = async () => {
    if (!selectedReason) return;
    const reason = selectedReason;
    setSelectedReason(null);
    setIsEnding(true);
    try {
      await Promise.resolve(onEndSession(reason));
    } catch (err) {
      console.error("Error ending session:", err);
      onError?.(err);
    } finally {
      setIsEnding(false);
    }
  };

  const handleClose = () => {
    if (isEnding) return;
    setSelectedReason(null);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={handleClose}
        aria-hidden="true"
      />
      {/* Modal - positioned below close button (top-10 + h-10 + 16px gap = 96px), right edge aligned */}
      <div
        className="fixed z-[9999] top-[96px] right-[30px] w-[432px] max-w-[calc(100vw-60px)] bg-white rounded-[14px] p-5 shadow-lg flex flex-col gap-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="end-session-title"
      >
        <div className="flex items-start justify-between gap-4">
          <h1
            id="end-session-title"
            className="text-[20px] font-bold text-text-primary flex-1 leading-tight"
          >
            Are you sure you would like to end this session?
          </h1>
          <button
            type="button"
            onClick={handleClose}
            className="shrink-0 p-1 -m-1 rounded hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <CloseIcon className="w-5 h-5 [&_path]:stroke-current" />
          </button>
        </div>
        <p className="text-[15px] text-text2 leading-[22px]">
          This will end the session. Your feedback{" "}
          <span className="font-bold">will not</span> be generated.
        </p>
        <p className="text-[15px] text-text2 font-medium">Select why:</p>
        <div className="grid grid-cols-2 gap-2">
          {END_REASON_OPTIONS.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              onClick={() => setSelectedReason(value)}
              className={`px-4 py-3 rounded-lg text-center text-[15px] font-medium transition-colors ${
                selectedReason === value
                  ? "bg-primary3 text-text-secondary border-2 border-primary3"
                  : "bg-background3 text-text-primary border-2 border-transparent hover:bg-gray-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="pt-8 flex justify-end">
          <Button
            variant="primary"
            loading={isEnding || loading}
            disabled={!selectedReason || isEnding || loading}
            onClick={handleEndSession}
            className="!h-[48px] !w-[calc((100%-8px)/2)] !min-w-[160px] !rounded-[24px] !bg-primary3 !text-text-secondary disabled:!text-text-secondary text-[16px] font-semibold hover:!opacity-80"
          >
            End Session
          </Button>
        </div>
      </div>
    </>
  );
}
